import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import AppContext from "../../../AppContext";
import botagIconPurpleContainer from "../../../assets/icons/botag-icon-purple-container.svg";
import { APP_TITLE, PENDING_BILLING_ACTIVATION_TOAST_KEY } from "../../../config/constant";
import PopupContext from "../../../PopupContext";
import * as Library from "../../../utils/Library";
import { setNavigations } from "../../../utils/redux/navigationAccessSlice";
import CardComponentContainer from "../../common/cardContainers/CardComponentContainer";
import StandardPopupBuilder from "../../common/dialogs/StandardPopupBuilder";
import ContainerLoader from "../../common/loaders/ContainerLoader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import StripePopup from "../../common/stripe/StripePopup";
import TermsAndPrivacyPanel from "../../common/termsandConditions/TermsAndPrivacyPanel";
import BillingPlans from "./billingPageComponents/BillingPlans";
import ContentSubscriptionActivation from "./billingPageComponents/ContentSubscriptionActivation";
import ContentSubscriptionConfirmation from "./billingPageComponents/ContentSubscriptionConfirmation";
import {
    requestInviteUpdate,
    isVerifiedActivation,
    requestSubscriptionReconciliation,
    requestStripePaymentMethod,
    requestSubscriptionActivation,
    requestSubscriptionPlanSet
} from "./billingPageComponents/billingPageApi";
import { BILLING_CADENCE, BILLING_PLAN_SET_ID } from "./billingPageComponents/billingPageConfig";

// #region SCOPED CSS
const StyledPage = styled.div`
    .cancel-subscription {
        width: 165px;
        height: 35px;
        padding: 10px;
        border-radius: 4px;
        background: #141922;
        font-size: 12px;
        cursor: pointer;
    }
    .cancel-subscription.disabled {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        color: var(--text-and-icons-gray-200, #565656);
        cursor: not-allowed;
    }
`;
// #endregion SCOPED CSS

// #region CONSTANTS
const BILLING_POPUP_CSS = `
    .CardBox {
        max-height: calc(100vh - 32px);
        max-width: calc(100vw - 32px);
        overflow-y: auto;
    }

    .CardBox-container {
        box-sizing: border-box;
        max-width: calc(100vw - 32px);
        padding: 0 30px 32px;
    }

    .ready-delete {
        line-height: 29px;
        margin: 32px 0 22px;
    }

    .billing-popup-icon {
        display: inline-flex;
        isolation: isolate;
        position: relative;
    }

    .billing-popup-icon::before {
        background: linear-gradient(
            225deg,
            #60E25D 0%,
            #60E25D 28.1884%,
            #6123E4 49.9787%,
            #5AC3FE 63%,
            #22FFFF 99.5%
        );
        border-radius: 50%;
        content: "";
        filter: blur(13px);
        inset: -1px;
        opacity: 1;
        position: absolute;
        z-index: 0;
    }

    .billing-popup-icon img {
        height: 60px;
        position: relative;
        width: 60px;
        z-index: 1;
    }

    .CardBox-buttons {
        margin: 0 auto;
        max-width: 100%;
        width: 386px;
    }

    .CardBox-button-a {
        height: 44px;
    }

    @media (max-width: 480px) {
        .CardBox-container {
            padding-left: 16px;
            padding-right: 16px;
        }

        .CardBox-buttons {
            flex-direction: column;
            width: 100%;
        }

        .CardBox-buttons button {
            width: 100% !important;
        }
    }
`;

const REQUIRED_BILLING_PLAN_COUNT = 3;
// #endregion CONSTANTS

export default function BillingPage() {
    document.title = `${APP_TITLE} - Billing`;

    // #region VARS
    const navigate = useNavigate();
    const { MainLayoutProps } = useOutletContext();
    const { isLoadingApp } = React.useContext(AppContext);
    const { setGenericConfirmationPopup, setShowStripePopup } = React.useContext(PopupContext);
    const navigations = useSelector(state => state.navigationAccesses);
    const dispatch = useDispatch();

    const organizationID = localStorage.getItem("currentOrganizationID");
    const companyID = localStorage.getItem("currentCompanyID");
    const BOstagesAccountID = localStorage.getItem("BOstagesAccountID");
    // #endregion VARS

    // #region STATES
    const [availableSubscriptionPlans, setAvailableSubscriptionPlans] = useState([]);
    const [isLoadingPlans, setIsLoadingPlans] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const [legalPanelType, setLegalPanelType] = useState(null);
    const [subscriptionPlanForStripe, setSubscriptionPlanForStripe] = useState(null);
    const [currentSubscription, setCurrentSubscription] = useState({
        subscriptionPlanIDs: [], subscriptionPlanTime: "", subscriptionPlanStatus: ""
    });
    const [isReconciling, setIsReconciling] = useState(true);
    const checkoutBusy = useRef(false);
    const reconciliationBusy = useRef(false);
    const pendingPlan = useRef(null);
    // #endregion STATES

    // #region COMPUTED PROPS
    const storedSubscriptionPlanID =
        currentSubscription.subscriptionPlanIDs.length === 1 ? currentSubscription.subscriptionPlanIDs[0] : null;
    const activeSubscriptionPlanID =
        currentSubscription.subscriptionPlanStatus === "Active" &&
        availableSubscriptionPlans.some(
            subscriptionPlan =>
                subscriptionPlan._id === storedSubscriptionPlanID &&
                currentSubscription.subscriptionPlanTime.toLowerCase() === "quarterly" &&
                subscriptionPlan?.quarterlyPlan
        )
            ? storedSubscriptionPlanID
            : null;
    const hasActiveSubscription = !!activeSubscriptionPlanID;
    const displayedTermsAccepted = hasActiveSubscription || isTermsAccepted;
    // #endregion COMPUTED PROPS

    // #region METHODS
    function handleTermsChange() {
        setIsTermsAccepted(currentValue => !currentValue);
    }

    function handlePlanSelected(plan) {
        if (hasActiveSubscription) return;

        const isPlanAvailable =
            !!plan?.subscriptionPlanID &&
            availableSubscriptionPlans.some(subscriptionPlan => subscriptionPlan._id === plan.subscriptionPlanID);

        if (!isPlanAvailable) {
            Library.showToastMessage({
                type: "error",
                title: "Plan unavailable",
                message: "The selected subscription plan is not available. Please refresh the page and try again."
            });
            return;
        }

        if (!isTermsAccepted) {
            Library.showToastMessage({
                type: "warning",
                title: "Terms required",
                message: "Please accept the Terms and Conditions and Privacy Policy before continuing."
            });
            return;
        }

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            headingText: "CONFIRM YOUR SUBSCRIPTION",
            headingColor: "var(--text-icons-blue-500, #00aeef)",
            headingFontSize: "18px",
            useIconSrc: (
                <div className="billing-popup-icon">
                    <img alt="BOtag" src={botagIconPurpleContainer} />
                </div>
            ),
            title: <span>{plan.title}</span>,
            titleColor: "var(--text-icons-gray-100, #d1d1d1)",
            useAlternativeLayout: <ContentSubscriptionConfirmation confirmation={plan.confirmation} />,
            width: "560px",
            buttonWidth: "188px",
            buttonFontSize: "14px",
            customCSS: BILLING_POPUP_CSS,
            confirmText: "LET’S BO!",
            cancelText: "BACK",
            confirmState: "secondary",
            cancelState: "secondary",
            onConfirmClicked: () => handleSubscriptionCheckout(plan)
        });
    }

    async function handleSubscriptionCheckout(plan, savedStripePaymentMethodID = null) {
        if (checkoutBusy.current || reconciliationBusy.current) return;
        if (!plan?.subscriptionPlanID) {
            Library.showToastMessage({
                type: "error",
                title: "Invalid subscription plan",
                message: "The selected subscription plan does not contain valid plan IDs."
            });
            return;
        }

        checkoutBusy.current = true;
        setIsSubscribing(true);
        const handleRequestError = () => { checkoutBusy.current = false; setIsSubscribing(false); };

        try {
            let stripePaymentMethodID = savedStripePaymentMethodID;

            if (!stripePaymentMethodID) {
                const companyHasStripeCustomerID = localStorage.getItem("companyHasStripeCustomerID") === "true";

                if (companyHasStripeCustomerID) {
                    const paymentMethodData = await requestStripePaymentMethod({
                        companyID,
                        onError: handleRequestError
                    });

                    if (!paymentMethodData) return;

                    if (!paymentMethodData.hasPaymentMethods) {
                        setSubscriptionPlanForStripe(plan);
                        setIsSubscribing(false);
                        setShowStripePopup(true);
                        return;
                    }

                    stripePaymentMethodID = paymentMethodData.stripePaymentMethodID;
                } else {
                    setSubscriptionPlanForStripe(plan);
                    setIsSubscribing(false);
                    setShowStripePopup(true);
                    return;
                }
            }

            const subscriptionData = await requestSubscriptionActivation({
                organizationID,
                subscriptionPlanIDs: [plan.subscriptionPlanID],
                subscriptionPlanTime: BILLING_CADENCE,
                stripePaymentMethodID,
                BOstagesAccountID,
                onError: handleRequestError
            });

            if (!subscriptionData) return;
            if (!isVerifiedActivation(subscriptionData)) {
                pendingPlan.current = plan;
                showPaymentStatus(subscriptionData);
                return;
            }
            handleSubscriptionSuccess(plan, subscriptionData);
        } finally {
            checkoutBusy.current = false;
            setIsSubscribing(false);
        }
    }

    function handleStripePaymentMethodFinished(isSuccess, stripePaymentMethodID) {
        if (!isSuccess || !stripePaymentMethodID || !subscriptionPlanForStripe) {
            Library.showToastMessage({
                type: "error",
                title: "Payment method error",
                message: "Cannot continue without a valid payment method. Please add a payment method and try again."
            });
            return;
        }

        const selectedPlan = subscriptionPlanForStripe;
        setSubscriptionPlanForStripe(null);
        handleSubscriptionCheckout(selectedPlan, stripePaymentMethodID);
    }

    function handleSubscriptionSuccess(plan, subscriptionData) {
        if (!isVerifiedActivation(subscriptionData) || !subscriptionData.subscriptionPlanIDs.includes(plan.subscriptionPlanID)) return;
        const subscriptionPlanIDs = subscriptionData.subscriptionPlanIDs;
        const trialPeriod = subscriptionData?.trialDaysLeft ?? 0;

        localStorage.setItem("billingPlan", plan.title);
        localStorage.setItem("subscriptionPlanIDs", JSON.stringify(subscriptionPlanIDs));
        localStorage.setItem("subscriptionPlanTime", subscriptionData.subscriptionPlanTime);
        localStorage.setItem("subscriptionPlanStatus", "Active");
        localStorage.setItem("trialPeriod", trialPeriod);

        setCurrentSubscription({
            subscriptionPlanIDs,
            subscriptionPlanTime: subscriptionData.subscriptionPlanTime,
            subscriptionPlanStatus: "Active"
        });

        updateNavigationAccess();
        updateInviteIfNeeded();

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            headingText: "ACCOUNT ACTIVATED",
            headingColor: "var(--text-icons-blue-500, #00aeef)",
            headingFontSize: "24px",
            useIconSrc: (
                <div className="billing-popup-icon">
                    <img alt="BOtag" src={botagIconPurpleContainer} />
                </div>
            ),
            title: <span>Welcome to {plan.title}</span>,
            titleColor: "var(--text-icons-gray-100, #d1d1d1)",
            useAlternativeLayout: <ContentSubscriptionActivation activationSuccess={plan.activationSuccess} />,
            width: "560px",
            buttonWidth: "188px",
            buttonFontSize: "14px",
            customCSS: BILLING_POPUP_CSS,
            showCancel: false,
            confirmText: "CONTINUE",
            confirmState: "secondary",
            onConfirmClicked: () => handleContinueCTA(plan)
        });
    }

    function updateNavigationAccess() {
        const navigationAccesses = navigations?.navigationAccesses || {};
        const updatedNavigations = {
            ...navigationAccesses,
            organization: {
                ...navigationAccesses.organization,
                navigationPanel: true,
                createModeOnly: true
            }
        };

        dispatch(setNavigations({ navigationAccesses: updatedNavigations }));
    }

    function updateInviteIfNeeded() {
        const invite = getStoredObject("invite");
        if (!invite?._id || invite?.status?.status !== "Pre-Enrolled") return;

        requestInviteUpdate({ inviteID: invite._id }).then(isUpdated => {
            if (isUpdated) localStorage.setItem("status", "Enrolling");
        });
    }

    function handleContinueCTA(plan) {
        const toast = plan?.activationSuccess?.toast;
        const currentOrganizationStatus = localStorage.getItem("currentOrganizationStatus");

        if (currentOrganizationStatus === "Active") {
            persistActivationToast(toast);
            navigate("/dashboard");
            window.location.reload();
            return;
        }

        if (toast) Library.showToastMessage({ ...toast, type: "success" });
        navigate("/account/create");
    }
    // #endregion METHODS

    function showPaymentStatus(data) {
        const requiresPayment = data.result === "paymentRequired";
        const paymentURL = requiresPayment ? data.paymentURL : null;
        Library.showConfirmationMessage({
            setGenericConfirmationPopup, useV2: true,
            headingText: requiresPayment ? "PAYMENT REQUIRED" : "PAYMENT NOT CONFIRMED",
            message: data.message || (requiresPayment
                ? "Complete the payment authentication to activate your subscription."
                : "Your existing plan is unchanged. Refresh Billing to reconcile the payment, or contact support."),
            confirmText: paymentURL ? "COMPLETE PAYMENT" : "OK",
            showCancel: Boolean(paymentURL), cancelText: "NOT NOW",
            onConfirmClicked: () => { if (paymentURL) Library.openNewPage(paymentURL); }
        });
    }

    // Reconciliation only reads Stripe and finalizes verified payment. It cannot
    // create subscriptions or initiate charges, including on focus/auth returns.
    useEffect(() => {
        let mounted = true;
        const reconcile = async () => {
            if (checkoutBusy.current || reconciliationBusy.current) return;
            reconciliationBusy.current = true;
            const finish = () => { reconciliationBusy.current = false; if (mounted) setIsReconciling(false); };
            try {
                const data = await requestSubscriptionReconciliation({ organizationID, BOstagesAccountID, onError: finish });
                if (!mounted || !data) return;
                if (isVerifiedActivation(data)) {
                    const state = { subscriptionPlanIDs: data.subscriptionPlanIDs,
                        subscriptionPlanTime: data.subscriptionPlanTime, subscriptionPlanStatus: data.subscriptionPlanStatus };
                    setCurrentSubscription(state);
                    Object.entries(state).forEach(([key, value]) => localStorage.setItem(key, Array.isArray(value) ? JSON.stringify(value) : value));
                    const plan = pendingPlan.current;
                    if (plan) { pendingPlan.current = null; handleSubscriptionSuccess(plan, data); }
                } else if (data.result !== "unchanged") showPaymentStatus(data);
            } finally { finish(); }
        };
        const visible = () => { if (document.visibilityState === "visible") reconcile(); };
        reconcile();
        window.addEventListener("focus", reconcile);
        document.addEventListener("visibilitychange", visible);
        return () => { mounted = false; window.removeEventListener("focus", reconcile); document.removeEventListener("visibilitychange", visible); };
        // Reconcile against server authority whenever the organization changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationID, BOstagesAccountID]);

    // #region EFFECTS
    useEffect(() => {
        let isMounted = true;
        const handleLoader = isLoading => {
            if (isMounted) setIsLoadingPlans(isLoading);
        };

        setAvailableSubscriptionPlans([]);
        requestSubscriptionPlanSet({
            organizationID,
            subscriptionPlanSetID: BILLING_PLAN_SET_ID,
            setLoader: handleLoader
        }).then(data => {
            if (!isMounted || data === undefined) return;

            try {
                setAvailableSubscriptionPlans(normalizeSubscriptionPlans(data?.subscriptionPlans));
            } catch {
                setAvailableSubscriptionPlans([]);
                Library.showToastMessage({
                    type: "error",
                    title: "Subscription plans unavailable",
                    description: "An error occurred while loading subscription plans. Please try again later."
                });
            }
        });

        return () => {
            isMounted = false;
        };
    }, [organizationID]);

    useEffect(() => {
        setupHeaders({
            setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
            setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
            setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
            setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties
        });
    }, [
        MainLayoutProps?.setControlBarLeftBtns,
        MainLayoutProps?.setControlBarCenterBtns,
        MainLayoutProps?.setControlBarRightBtns,
        MainLayoutProps?.setFilterHeaderProperties,
        currentSubscription.subscriptionPlanTime
    ]);
    // #endregion EFFECTS

    // #region RENDER
    return (
        <StyledPage>
            <FullScreenOverlay show={!!legalPanelType}>
                {legalPanelType && (
                    <TermsAndPrivacyPanel type={legalPanelType} onCloseClicked={() => setLegalPanelType(null)} />
                )}
            </FullScreenOverlay>

            <FullScreenOverlay show={isSubscribing}>
                <StandardPopupBuilder
                    heading="SUBSCRIBING..."
                    headingColor="var(--text-icons-green-500)"
                    loading={true}
                    showCancel={false}
                    showConfirm={false}
                />
            </FullScreenOverlay>

            <StripePopup onSuccess={handleStripePaymentMethodFinished} showDefaultSetupPopup={false} />

            <ContainerLoader
                isLoading={!isLoadingApp && isLoadingPlans}
                isLoadingOverlay={false}
                className="w-100"
                alignPageCenter={true}>
                <CardComponentContainer
                    title="Subscription Plans"
                    style={{ paddingTop: "14px" }}
                    rightComponent={
                        hasActiveSubscription ? (
                            <div className="cancel-subscription disabled">CANCEL A SUBSCRIPTION</div>
                        ) : null
                    }>
                    <BillingPlans
                        style={{ paddingTop: "26px" }}
                        availableSubscriptionPlans={availableSubscriptionPlans}
                        activeSubscriptionPlanID={activeSubscriptionPlanID}
                        hasActiveSubscription={hasActiveSubscription}
                        isTermsAccepted={displayedTermsAccepted}
                        isInteractionDisabled={isLoadingPlans || isSubscribing || isReconciling || hasActiveSubscription}
                        onTermsChange={handleTermsChange}
                        onTermsClick={() => setLegalPanelType("terms")}
                        onPrivacyClick={() => setLegalPanelType("privacy")}
                        onPlanSelected={handlePlanSelected}
                    />
                </CardComponentContainer>
            </ContainerLoader>
        </StyledPage>
    );
    // #endregion RENDER
}

// #region HEADERS
function setupHeaders(props) {
    props.setControlBarLeftBtns?.([{ to: "#", disabled: true, title: "Invoices", icon: "fa-magnifying-glass" }]);
    props.setControlBarCenterBtns?.([{ to: "/billing", active: true, disabled: false, title: "BILLING" }]);

    const billingControlBarEnabled = localStorage.getItem("companyHasStripeCustomerID") === "true";

    props.setControlBarRightBtns?.([
        { to: "/billing", active: true, disabled: false, title: "Plans", icon: "file-check-01" },
        {
            to: "/billing/invoices",
            active: false,
            disabled: !billingControlBarEnabled,
            title: "Invoices",
            icon: "file-check-02"
        },
        { to: "/billing/reports", active: false, disabled: true, title: "Subs", icon: "file-03" },
        {
            to: "/billing/methods",
            active: false,
            disabled: !billingControlBarEnabled,
            title: "Methods",
            icon: "credit-card-check"
        }
    ]);

    props.setFilterHeaderProperties?.({
        visible: false,
        gridEnabled: true,
        listEnabled: true,
        onRefreshAction: undefined
    });
}
// #endregion HEADERS

// #region GLOBAL METHODS
function normalizeSubscriptionPlans(subscriptionPlans) {
    if (!Array.isArray(subscriptionPlans) || subscriptionPlans.length < REQUIRED_BILLING_PLAN_COUNT)
        throw new Error("At least three subscription plans are required.");

    const normalizedSubscriptionPlans = subscriptionPlans
        .slice(0, REQUIRED_BILLING_PLAN_COUNT)
        .map((subscriptionPlan, planIndex) => {
            const subscriptionPlanID = subscriptionPlan?._id;
            const displayName = subscriptionPlan?.displayName;
            const quarterlyPlan = subscriptionPlan?.quarterlyPlan;
            const quarterlyPrice = quarterlyPlan?.price;
            const currency = quarterlyPlan?.currency;
            const includedReports =
                planIndex === 0 ? quarterlyPlan?.freeActivationReports : quarterlyPlan?.freeReports;
            const hasValidID = typeof subscriptionPlanID === "string" && subscriptionPlanID.trim() !== "";
            const hasValidDisplayName = typeof displayName === "string" && displayName.trim() !== "";
            const hasValidCurrency = typeof currency === "string" && currency.trim() !== "";
            const hasValidPrice =
                typeof quarterlyPrice === "number" && Number.isFinite(quarterlyPrice) && quarterlyPrice >= 0;
            const hasValidIncludedReports = Number.isInteger(includedReports) && includedReports >= 0;

            if (
                !hasValidID ||
                !hasValidDisplayName ||
                !hasValidCurrency ||
                !hasValidPrice ||
                !hasValidIncludedReports
            )
                throw new Error("Invalid subscription plan data.");

            return {
                _id: subscriptionPlanID.trim(),
                displayName: displayName.trim(),
                quarterlyPlan,
                currency: currency.trim().toUpperCase(),
                includedReports,
                quarterlyPrice,
                monthlyPrice: quarterlyPrice / 3
            };
        });

    const subscriptionPlanIDs = normalizedSubscriptionPlans.map(subscriptionPlan => subscriptionPlan._id);
    if (new Set(subscriptionPlanIDs).size !== subscriptionPlanIDs.length)
        throw new Error("Subscription plan IDs must be unique.");

    return normalizedSubscriptionPlans;
}

function persistActivationToast(toast) {
    if (!toast?.title) return;

    try {
        sessionStorage.setItem(PENDING_BILLING_ACTIVATION_TOAST_KEY, JSON.stringify(toast));
    } catch {
        Library.showToastMessage({ ...toast, type: "success" });
    }
}

function getStoredObject(storageKey) {
    const storedValue = getStoredJSON(storageKey, null);
    return storedValue && typeof storedValue === "object" && !Array.isArray(storedValue) ? storedValue : null;
}

function getStoredJSON(storageKey, fallbackValue) {
    try {
        const storedValue = localStorage.getItem(storageKey);
        return storedValue ? JSON.parse(storedValue) : fallbackValue;
    } catch {
        return fallbackValue;
    }
}
// #endregion GLOBAL METHODS
